// Realistic 3D lobby backdrop — a tactical training yard shot from a low angle.
// Uses real materials, warm sunlight, distant architecture, moving smoke,
// birds and small ambient lights. Cinematic camera drifts slowly.
import { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { ContactShadows, Environment } from "@react-three/drei";
import * as THREE from "three";

function CameraDrift() {
    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        state.camera.position.x = Math.sin(t * 0.03) * 0.4;
        state.camera.position.y = 1.65 + Math.sin(t * 0.06) * 0.05;
        state.camera.position.z = 4.6 + Math.cos(t * 0.02) * 0.3;
        state.camera.lookAt(0, 1.35, 0);
    });
    return null;
}

function Ground() {
    return (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
            <planeGeometry args={[60, 60]} />
            <meshStandardMaterial color="#22201d" roughness={0.95} metalness={0.1} />
        </mesh>
    );
}

function ConcretePad() {
    return (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]} receiveShadow>
            <planeGeometry args={[14, 14]} />
            <meshStandardMaterial color="#3a3835" roughness={0.9} metalness={0.15} />
        </mesh>
    );
}

function Container({ position, size = [3, 2.6, 6], color = "#5a4a34" }) {
    return (
        <mesh position={position} castShadow receiveShadow>
            <boxGeometry args={size} />
            <meshStandardMaterial color={color} roughness={0.85} metalness={0.35} />
        </mesh>
    );
}

function Building({ position, size = [8, 6, 4], color = "#3a3833" }) {
    return (
        <mesh position={position} castShadow receiveShadow>
            <boxGeometry args={size} />
            <meshStandardMaterial color={color} roughness={0.9} metalness={0.2} />
        </mesh>
    );
}

function Barrel({ position }) {
    return (
        <mesh position={position} castShadow receiveShadow>
            <cylinderGeometry args={[0.35, 0.35, 1.1, 12]} />
            <meshStandardMaterial color="#4a3320" roughness={0.85} metalness={0.4} />
        </mesh>
    );
}

function Flag({ position }) {
    const flagRef = useRef();
    useFrame((state) => {
        if (!flagRef.current) return;
        const t = state.clock.getElapsedTime();
        flagRef.current.rotation.y = Math.sin(t * 1.4) * 0.35;
        flagRef.current.rotation.z = Math.sin(t * 0.9) * 0.06;
    });
    return (
        <group position={position}>
            {/* Pole */}
            <mesh position={[0, 2.5, 0]} castShadow>
                <cylinderGeometry args={[0.03, 0.03, 5, 8]} />
                <meshStandardMaterial color="#2a2a2a" metalness={0.8} roughness={0.3} />
            </mesh>
            <mesh position={[0, 5.1, 0]} castShadow>
                <sphereGeometry args={[0.06, 8, 8]} />
                <meshStandardMaterial color="#2a2a2a" metalness={0.9} roughness={0.2} />
            </mesh>
            {/* Flag cloth */}
            <mesh ref={flagRef} position={[0.5, 4.4, 0]} castShadow>
                <planeGeometry args={[1.1, 0.7]} />
                <meshStandardMaterial color="#a8621a" side={THREE.DoubleSide} roughness={0.9} />
            </mesh>
        </group>
    );
}

function DistantSmoke() {
    // Big low-opacity billboards drifting horizontally — cheap "smoke plumes"
    const ref = useRef();
    useFrame((state) => {
        if (!ref.current) return;
        const t = state.clock.getElapsedTime();
        ref.current.children.forEach((c, i) => {
            c.position.x = (i * 3 + t * 0.4) % 24 - 12;
            c.material.opacity = 0.08 + Math.sin(t * 0.5 + i) * 0.03;
        });
    });
    return (
        <group ref={ref} position={[0, 4, -14]}>
            {[0, 1, 2, 3].map((i) => (
                <mesh key={i} position={[i * 3 - 5, 0, 0]}>
                    <planeGeometry args={[6, 3]} />
                    <meshBasicMaterial
                        color="#b8a680"
                        transparent
                        opacity={0.09}
                        depthWrite={false}
                    />
                </mesh>
            ))}
        </group>
    );
}

function Birds() {
    const groupRef = useRef();
    useFrame((state) => {
        if (!groupRef.current) return;
        const t = state.clock.getElapsedTime();
        groupRef.current.children.forEach((bird, i) => {
            bird.position.x = -20 + ((t * 1.2 + i * 6) % 40);
            bird.position.y = 8 + Math.sin(t * 0.8 + i) * 0.4;
            bird.position.z = -18 + i * 2;
        });
    });
    return (
        <group ref={groupRef}>
            {[0, 1, 2].map((i) => (
                <mesh key={i}>
                    <boxGeometry args={[0.18, 0.02, 0.08]} />
                    <meshBasicMaterial color="#1a1a1a" />
                </mesh>
            ))}
        </group>
    );
}

function BlinkLight({ position, color = "#ff5a3a", period = 1.6 }) {
    const ref = useRef();
    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        const on = Math.sin(t * (Math.PI * 2) / period) > 0.4;
        if (ref.current) ref.current.intensity = on ? 1.6 : 0.05;
    });
    return <pointLight ref={ref} position={position} color={color} intensity={1} distance={6} />;
}

function DistantSilhouettes() {
    // Extremely small NPC dots far back — implies life without heavy geometry
    const ref = useRef();
    useFrame((state) => {
        if (!ref.current) return;
        const t = state.clock.getElapsedTime();
        ref.current.children.forEach((npc, i) => {
            npc.position.x = -15 + i * 5 + Math.sin(t * 0.3 + i) * 0.4;
        });
    });
    return (
        <group ref={ref} position={[0, 0.8, -13]}>
            {[0, 1, 2, 3].map((i) => (
                <mesh key={i}>
                    <capsuleGeometry args={[0.14, 0.5, 4, 8]} />
                    <meshStandardMaterial color="#141210" roughness={0.9} />
                </mesh>
            ))}
        </group>
    );
}

function TacticalDrone() {
    const ref = useRef();
    useFrame((state) => {
        if (!ref.current) return;
        const t = state.clock.getElapsedTime();
        ref.current.position.x = Math.sin(t * 0.15) * 8;
        ref.current.position.y = 6.5 + Math.sin(t * 0.4) * 0.2;
        ref.current.position.z = -8 + Math.cos(t * 0.15) * 3;
        ref.current.rotation.y = t * 0.5;
    });
    return (
        <group ref={ref}>
            <mesh castShadow>
                <boxGeometry args={[0.5, 0.15, 0.5]} />
                <meshStandardMaterial color="#141212" metalness={0.6} roughness={0.4} />
            </mesh>
            {/* Blinking indicator */}
            <BlinkLight position={[0, -0.05, 0]} color="#ff3030" period={0.9} />
        </group>
    );
}

function Scene() {
    return (
        <>
            <fog attach="fog" args={["#1a1712", 20, 60]} />
            <ambientLight intensity={0.35} color="#f2ddb0" />

            {/* Warm sunset key light */}
            <directionalLight
                position={[-12, 16, 6]}
                intensity={1.35}
                color="#ffd8a3"
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
                shadow-camera-left={-16}
                shadow-camera-right={16}
                shadow-camera-top={16}
                shadow-camera-bottom={-16}
            />
            {/* Cool fill from opposite side */}
            <directionalLight position={[12, 8, -4]} intensity={0.35} color="#8fb0d8" />

            <hemisphereLight color="#f4dbaa" groundColor="#221812" intensity={0.4} />

            <Ground />
            <ConcretePad />

            {/* Foreground containers */}
            <Container position={[-6.5, 1.3, -2.5]} size={[2.6, 2.6, 5]} color="#5a4a34" />
            <Container position={[6.5, 1.3, -2.5]} size={[2.6, 2.6, 5]} color="#3f3830" />
            <Container position={[-6.5, 3.9, -2.5]} size={[2.6, 2.6, 5]} color="#3f3830" />

            {/* Mid-ground pallets */}
            <Barrel position={[-3.5, 0.55, -5.5]} />
            <Barrel position={[-3.0, 0.55, -6.2]} />
            <Barrel position={[3.2, 0.55, -6]} />

            {/* Distant buildings + main structure */}
            <Building position={[-12, 3, -10]} size={[6, 6, 4]} color="#3a3833" />
            <Building position={[12, 4, -12]} size={[8, 8, 5]} color="#2c2b28" />
            <Building position={[0, 5, -14]} size={[12, 10, 3]} color="#2f2e2b" />

            {/* Windows on the main hangar (small emissive squares) */}
            {[-3, -1, 1, 3].map((x, i) => (
                <mesh key={`w-${i}`} position={[x, 6, -12.5]}>
                    <boxGeometry args={[0.4, 0.4, 0.05]} />
                    <meshBasicMaterial color="#ffb060" />
                </mesh>
            ))}
            {/* Roof lights blinking */}
            <BlinkLight position={[-11, 6.5, -10]} color="#ff3030" period={2.2} />
            <BlinkLight position={[13, 8.5, -12]} color="#ffb060" period={1.6} />

            {/* Flags */}
            <Flag position={[-9, 0, -6]} />
            <Flag position={[9, 0, -6]} />

            {/* Life */}
            <DistantSmoke />
            <Birds />
            <TacticalDrone />
            <DistantSilhouettes />

            <ContactShadows position={[0, 0.01, 0]} opacity={0.55} blur={2.4} scale={20} far={8} />

            <Environment preset="sunset" />
            <CameraDrift />
        </>
    );
}

export default function LobbyBackground() {
    return (
        <div className="absolute inset-0" style={{ zIndex: 0 }}>
            <Canvas
                shadows
                camera={{ position: [0, 1.65, 4.6], fov: 42 }}
                gl={{
                    antialias: true,
                    powerPreference: "high-performance",
                    toneMapping: THREE.ACESFilmicToneMapping,
                    toneMappingExposure: 1.05,
                }}
                dpr={[1, 1.5]}
            >
                <color attach="background" args={["#0e0c09"]} />
                <Suspense fallback={null}>
                    <Scene />
                </Suspense>
            </Canvas>
            {/* Cinematic bar-style vignette (readability under UI) */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at center 40%, transparent 20%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.85) 100%)",
                }}
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 22%, transparent 55%, rgba(0,0,0,0.92) 100%)",
                }}
            />
        </div>
    );
}
